import { z } from "zod";
import { businessMetadataShape } from "./business-metadata";
import { localizedSchema, pricingFactorSchema, scenarioSchema } from "./schemas";
import { defaultScenario, scenarioQuestions } from "./business-truth";
import type { BusinessTruth, Discovery, DiscoveryAnswers, Product } from "./types";
import { isNumericPricing } from "./types";
import {
  linearNavigation,
  navigationSchema,
  resolveDiscoveryPath,
  validateNavigation,
} from "./discovery-navigation";

export const MAX_MODEL_BYTES = 256_000;
const selectionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("fixed"), offeringId: z.string().nullable() }).strict(),
  z
    .object({
      kind: z.literal("question"),
      questionId: z.string(),
      options: z.record(z.string(), z.string()),
      fallbackId: z.string().nullable(),
    })
    .strict(),
]);
const pricingSchema = z
  .object({
    currency: z.string().length(3),
    display: z.enum(["range", "from", "fixed", "hidden"]),
    low: z.number().finite().nonnegative(),
    high: z.number().finite().nonnegative(),
    fixed: z.number().finite().nonnegative().optional(),
    factors: z.array(pricingFactorSchema),
  })
  .strict();
const runtimePricingSchema = z.union([pricingSchema, z.object({
  availability: z.literal("quote-required"), currency: z.string().length(3), factors: z.array(pricingFactorSchema).max(0),
  display: z.never().optional(), low: z.never().optional(), high: z.never().optional(), fixed: z.never().optional(),
}).strict()]);
const offeringSchema = z
  .object({
    id: z.string().min(1),
    name: localizedSchema,
    description: localizedSchema,
    pricing: runtimePricingSchema,
  })
  .strict();
export const executableModelSchema = z
  .object({
    schemaVersion: z.literal(1),
    runtimeVersion: z.literal(1),
    business: z
      .object({
        ...businessMetadataShape,
        name: localizedSchema,
        website: z.string().url(),
        contact: z.object({ phone: z.string(), email: z.string() }).strict(),
        serviceAreas: z.array(localizedSchema),
        primaryLanguage: z.string().min(2),
        currency: z.string().length(3),
      })
      .strict(),
    scenario: scenarioSchema.omit({ status: true }),
    navigation: navigationSchema.optional(),
    products: z.array(offeringSchema),
    services: z.array(offeringSchema),
    selection: z.object({ product: selectionSchema, service: selectionSchema }).strict(),
  })
  .strict();
export type ExecutableDiscoveryModel = z.infer<typeof executableModelSchema>;
export type RuntimeOffering = ExecutableDiscoveryModel["products"][number];
export type OfferingSelection = z.infer<typeof selectionSchema>;
export type PublishedDiscoveryModel = {
  id: string;
  /** Sequential deployment version; absent on legacy envelopes and local previews. */
  versionNumber?: number;
  /** Immutable Context snapshot in this customer's context collection. */
  contextId?: string;
  schemaVersion: 1;
  runtimeVersion: 1;
  publishedAt: Date;
  publishedAtRevision: number;
  model: ExecutableDiscoveryModel;
};

function unique(ids: string[], where: string) {
  if (new Set(ids).size !== ids.length) throw new Error(`Duplicate IDs in ${where}.`);
}
export function validateDiscoveryModel(value: unknown): ExecutableDiscoveryModel {
  const model = executableModelSchema.parse(value);
  if (new TextEncoder().encode(JSON.stringify(model)).length > MAX_MODEL_BYTES)
    throw new Error(`Discovery model exceeds ${MAX_MODEL_BYTES} bytes.`);
  const questions = model.scenario.steps.flatMap((step) => step.questions);
  if (!questions.length) throw new Error("Discovery requires questions.");
  unique(
    model.scenario.steps.map((s) => s.id),
    "steps",
  );
  if (model.scenario.steps.some((s) => !s.questions.length))
    throw new Error("Discovery steps cannot be empty.");
  unique(
    questions.map((q) => q.id),
    "questions",
  );
  for (const question of questions) {
    if ("options" in question)
      unique(
        question.options.map((o) => o.id),
        `options of ${question.id}`,
      );
    else if (
      !Number.isFinite(question.min) ||
      !Number.isFinite(question.max) ||
      question.min > question.max
    )
      throw new Error(`Invalid slider ${question.id}.`);
  }
  if (Object.hasOwn(model, "navigation")) validateNavigation(model.navigation, questions);
  const requireText = (value: unknown, path: string) => {
    if (!value || typeof value !== "object") return;
    for (const [key, child] of Object.entries(value)) {
      if (
        ["name", "title", "label"].includes(key) &&
        child &&
        typeof child === "object" &&
        !String((child as Record<string, unknown>)[model.business.primaryLanguage] ?? "").trim()
      )
        throw new Error(`Missing primary-language text at ${path}.${key}.`);
      requireText(child, `${path}.${key}`);
    }
  };
  // Navigation dictionary keys are IDs, including IDs such as "title" or "name".
  const { navigation: _navigation, ...content } = model;
  requireText(content, "model");
  for (const [kind, offerings, references] of [
    ["product", model.products, model.scenario.productIds],
    ["service", model.services, model.scenario.serviceIds],
  ] as const) {
    unique(
      offerings.map((o) => o.id),
      kind,
    );
    unique(references, `${kind} references`);
    if (
      references.length !== offerings.length ||
      references.some((id) => !offerings.some((o) => o.id === id))
    )
      throw new Error(`Dangling ${kind} reference.`);
    const selection = model.selection[kind];
    const ids =
      selection.kind === "fixed"
        ? [selection.offeringId]
        : [selection.fallbackId, ...Object.values(selection.options)];
    if (ids.some((id) => id !== null && !offerings.some((o) => o.id === id)))
      throw new Error(`Missing ${kind} in selection.`);
    if (selection.kind === "question") {
      const q = questions.find((q) => q.id === selection.questionId);
      if (!q || (q.kind !== "cards" && q.kind !== "chips"))
        throw new Error(`Selection requires scalar question ${selection.questionId}.`);
      if (Object.keys(selection.options).some((id) => !q.options.some((o) => o.id === id)))
        throw new Error("Invalid selection option mapping.");
    }
    for (const offering of offerings) {
      const p = offering.pricing;
      if (p.currency !== model.business.currency) throw new Error(`Invalid pricing currency for ${offering.id}.`);
      if (!isNumericPricing(p)) continue;
      if (
        p.currency !== model.business.currency ||
        p.high < p.low ||
        (p.display === "fixed" && p.fixed === undefined)
      )
        throw new Error(`Invalid pricing for ${offering.id}.`);
      unique(
        p.factors.map((f) => f.id),
        `factors of ${offering.id}`,
      );
      for (const f of p.factors) {
        for (const n of [f.amount, f.low, f.high, f.multiplier])
          if (n !== undefined && !Number.isFinite(n)) throw new Error(`Nonfinite factor ${f.id}.`);
        const q = questions.find((q) => q.id === f.answerId);
        if ((f.answerId && !q) || (!f.answerId && f.answerValues?.length))
          throw new Error(`Dangling factor answerId: ${f.id}.`);
        if (
          f.answerValues?.some(
            (v) =>
              !q ||
              (q.kind === "slider"
                ? typeof v !== "number" || v < q.min || v > q.max
                : !("options" in q) || !q.options.some((o) => o.id === v)),
          )
        )
          throw new Error(`Invalid answerValues: ${f.id}.`);
        const condition = questions.find((q) => q.id === f.conditionAnswerId);
        if (f.conditionAnswerId !== undefined && (!condition || f.conditionAnswerId === f.answerId))
          throw new Error(`Invalid conditionAnswerId: ${f.id}.`);
        if (f.conditionAnswerValues !== undefined && !condition)
          throw new Error(`conditionAnswerValues without question: ${f.id}.`);
        if (f.conditionAnswerValues?.some((value) => !condition ||
          (condition.kind === "slider"
            ? typeof value !== "number" || !Number.isFinite(value) || value < condition.min || value > condition.max
            : !("options" in condition) || typeof value !== "string" || !condition.options.some((o) => o.id === value))))
          throw new Error(`Invalid conditionAnswerValues: ${f.id}.`);
        if (f.kind === "per-unit" && q?.kind !== "slider")
          throw new Error(`Per-unit factor ${f.id} needs a numeric question.`);
        if (f.kind === "multiplier") {
          if (
            f.multiplier === undefined ||
            f.multiplier <= 0 ||
            f.amount !== undefined ||
            f.low !== undefined ||
            f.high !== undefined
          )
            throw new Error(`Invalid multiplier ${f.id}.`);
        } else if (
          (f.amount !== undefined) === (f.low !== undefined && f.high !== undefined) ||
          (f.low === undefined) !== (f.high === undefined) ||
          (f.low !== undefined && f.high! < f.low) ||
          f.multiplier !== undefined
        )
          throw new Error(`Invalid additive factor ${f.id}.`);
      }
    }
  }
  return model;
}

/** Authoring-only adapter. Never used to interpret saved visitor answers. */
export function compileDiscoveryModel(truth: BusinessTruth): ExecutableDiscoveryModel {
  const source = defaultScenario(truth);
  const questions = scenarioQuestions(source);
  // Old system/rooms fallbacks depend on names and implicit precedence. Refuse an
  // ambiguous migration rather than silently changing an existing journey.
  if (questions.some((q) => q.id === "system"))
    throw new Error(
      "Legacy system selection requires explicit owner configuration before migration.",
    );
  const active = (items: Product[], ids: string[]) =>
    items
      .filter((o) => o.status === "active" && ids.includes(o.id))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
  const products = active(truth.products, source.productIds),
    services = active(truth.services, source.serviceIds);
  const select = (items: Product[], fallbackId: string | null): OfferingSelection => {
    const selectors = questions.filter(
      (q) =>
        (q.kind === "cards" || q.kind === "chips") &&
        q.options.some((o) => items.some((i) => i.id === o.id)),
    );
    if (selectors.length > 1)
      throw new Error(
        "Multiple implicit offering selectors require explicit configuration before migration.",
      );
    const q = selectors[0];
    if (!q || !("options" in q)) return { kind: "fixed", offeringId: fallbackId };
    return {
      kind: "question",
      questionId: q.id,
      options: Object.fromEntries(
        q.options.filter((o) => items.some((i) => i.id === o.id)).map((o) => [o.id, o.id]),
      ),
      fallbackId,
    };
  };
  const offering = (o: Product): RuntimeOffering => ({
    id: o.id,
    name: o.name,
    description: o.description,
    pricing: !isNumericPricing(o.pricing) ? { availability: "quote-required", currency: o.pricing.currency, factors: [] } : {
      currency: o.pricing.currency,
      display: o.pricing.display,
      low: o.pricing.low,
      high: o.pricing.high,
      ...(o.pricing.fixed !== undefined ? { fixed: o.pricing.fixed } : {}),
      factors: o.pricing.factors,
    },
  });
  const { status: _status, ...scenario } = source;
  const b = truth.business;
  return validateDiscoveryModel({
    schemaVersion: 1,
    runtimeVersion: 1,
    business: {
      name: b.name,
      website: b.website,
      contact: b.contact,
      serviceAreas: b.serviceAreas,
      primaryLanguage: b.primaryLanguage ?? "en",
      currency: b.currency,
      ...(b.countryCode !== undefined ? { countryCode: b.countryCode } : {}),
      ...(b.tax !== undefined ? { tax: b.tax } : {}),
    },
    scenario: {
      ...scenario,
      productIds: products.map((p) => p.id),
      serviceIds: services.map((s) => s.id),
    },
    navigation: linearNavigation(questions),
    products: products.map(offering),
    services: services.map(offering),
    selection: {
      product: select(products, products[0]?.id ?? null),
      service: select(services, !products.length && services.length === 1 ? services[0]!.id : null),
    },
  });
}

export function validateAnswers(
  model: ExecutableDiscoveryModel,
  answers: DiscoveryAnswers,
  complete = false,
): void {
  const questions = model.scenario.steps.flatMap((s) => s.questions);
  for (const [id, value] of Object.entries(answers)) {
    const q = questions.find((q) => q.id === id);
    if (!q) throw new Error(`Unknown question ${id}.`);
    if (value === undefined) continue;
    const valid =
      q.kind === "slider"
        ? typeof value === "number" && Number.isFinite(value) && value >= q.min && value <= q.max
        : "options" in q &&
          (q.kind === "multi"
            ? Array.isArray(value) &&
              new Set(value).size === value.length &&
              value.every((v) => q.options.some((o) => o.id === v))
            : typeof value === "string" && q.options.some((o) => o.id === value));
    if (!valid) throw new Error(`Invalid answer to ${id}.`);
  }
  if (
    complete &&
    resolveDiscoveryPath(model, answers).path.some(
      ({ question: q }) => q.required !== false && answers[q.id] === undefined,
    )
  )
    throw new Error("Required questions are unanswered.");
}

export function selectedOfferings(model: ExecutableDiscoveryModel, answers: DiscoveryAnswers): RuntimeOffering[] {
  answers = resolveDiscoveryPath(model, answers).answers;
  return (["product", "service"] as const).flatMap((kind) => {
    const selection = model.selection[kind];
    const answer = selection.kind === "question" ? answers[selection.questionId] : undefined;
    const id = selection.kind === "fixed" ? selection.offeringId : (typeof answer === "string" ? selection.options[answer] : undefined) ?? selection.fallbackId;
    return model[kind === "product" ? "products" : "services"].filter((o) => o.id === id);
  });
}
export function discoveryPricingStatus(model: ExecutableDiscoveryModel, answers: DiscoveryAnswers) {
  const selected = selectedOfferings(model, answers);
  if (!selected.length || selected.some((o) => !isNumericPricing(o.pricing))) return "quote-required" as const;
  if (model.scenario.pricingDisplay === "hidden" || selected.some((o) => isNumericPricing(o.pricing) && o.pricing.display === "hidden")) return "hidden" as const;
  return undefined;
}

export function validateJourney(model: ExecutableDiscoveryModel, discovery: Discovery): void {
  if (
    discovery.schemaVersion !== 2 ||
    !discovery.modelId ||
    !discovery.id ||
    discovery.id !== discovery.sessionId
  )
    throw new Error("Invalid journey binding.");
  if (
    !["in_progress", "completed", "abandoned"].includes(discovery.status) ||
    !["questions", "result", "contact", "sent"].includes(discovery.stage)
  )
    throw new Error("Invalid journey state.");
  if (discovery.revisionId !== undefined && !/^[a-f0-9-]{36}$/.test(discovery.revisionId))
    throw new Error("Invalid Discovery revision marker.");
  validateAnswers(model, discovery.answers, discovery.status === "completed");
  const questions = model.scenario.steps.flatMap((s) => s.questions);
  const route = resolveDiscoveryPath(model, discovery.answers);
  if (route.removedAnswerIds.length) throw new Error("Journey contains inactive answers.");
  if (
    !Number.isInteger(discovery.questionIndex) ||
    discovery.questionIndex < 0 ||
    discovery.questionIndex >= questions.length
  )
    throw new Error("Invalid journey progress.");
  if (
    discovery.scenarioId !== model.scenario.id ||
    discovery.presentedQuestions?.some((p) => !route.questionIds.has(p.questionId))
  )
    throw new Error("Journey does not match its model.");
  const current = route.path.find((entry) => entry.question.id === discovery.currentQuestionId);
  if (
    !current ||
    discovery.questionIndex !== current.questionIndex ||
    discovery.currentStepId !== current.stepId
  )
    throw new Error("Inconsistent journey progress.");
  const shown = discovery.presentedQuestions ?? [];
  if (
    shown.length > questions.length * 100 ||
    shown.some((p) => !/^[a-z]{2,3}$/.test(p.language)) ||
    new Set(shown.map((p) => `${p.questionId}/${p.language}`)).size !== shown.length
  )
    throw new Error("Invalid presented questions.");
  if (discovery.status === "completed" && !discovery.result)
    throw new Error("Completed journey is missing its saved result.");
  if (discovery.result) {
    const r = discovery.result;
    const e = r.estimate;
    if (discovery.status !== "completed") throw new Error("Invalid saved result.");
    if (e === null) {
      if (!["quote-required", "hidden"].includes(r.pricingStatus ?? ""))
        throw new Error("Unavailable estimate requires a pricing status.");
      if (r.pricingStatus !== discoveryPricingStatus(model, discovery.answers))
        throw new Error("Unavailable estimate does not match pricing policy.");
    } else if (!e || r.pricingStatus !== undefined ||
      selectedOfferings(model, discovery.answers).some((o) => !isNumericPricing(o.pricing)) ||
      ![e.low, e.high, e.confidence].every(Number.isFinite) || e.low < 0 || e.high < e.low ||
      e.confidence < 0 || e.confidence > 100 || e.currency !== model.business.currency) {
      throw new Error("Invalid saved estimate.");
    }
    if (
      !Array.isArray(r.recommendedProductIds) ||
      !Array.isArray(r.recommendedServiceIds) ||
      r.recommendedProductIds.length > 1 ||
      r.recommendedServiceIds.length > 1 ||
      r.recommendedProductIds.some((id) => !model.products.some((o) => o.id === id)) ||
      r.recommendedServiceIds.some((id) => !model.services.some((o) => o.id === id))
    )
      throw new Error("Saved recommendation does not match its model.");
  } else if (discovery.stage !== "questions")
    throw new Error("A result is required for this journey stage.");
}

export function sameExecutableModel(
  a: ExecutableDiscoveryModel,
  b: ExecutableDiscoveryModel,
): boolean {
  const stable = (v: unknown): string =>
    Array.isArray(v)
      ? `[${v.map(stable).join(",")}]`
      : v && typeof v === "object"
        ? `{${Object.entries(v)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([k, v]) => `${JSON.stringify(k)}:${stable(v)}`)
            .join(",")}}`
        : JSON.stringify(v);
  return stable(a) === stable(b);
}
